/**
 * `<xa-pie>` — pie chart (default variant B "mono ramp": Cyber Grape ramp
 * with % labels inside wedges). Registered as `xaPie`. Hovering a wedge
 * translates it 5px outward along its mid-angle (140ms, shared CSS). The
 * legend is NON-toggling (static spans, no `aria-pressed`).
 *
 * Bindings:
 * | Binding | Type | Default | Notes |
 * |---|---|---|---|
 * | `data` | `XaSlice[]` | — | required |
 * | `colors` | `XaPalette` | `'mono'` | global `xaChartsConfig.palette` wins over the default |
 * | `size` | `number` | `240` | square SVG px |
 * | `labels` | `'inside' \| 'leader' \| 'none'` | `'inside'` | inside: % at 0.62r, hidden < 7%; leader: `label · %` polylines, hidden < 4% |
 * | `legend` | `'bottom' \| 'none'` | `'bottom'` | static (non-toggling) legend |
 * | `on-slice-hover` | `&` callback | — | `$event: XaSliceHoverEvent` |
 * | `on-slice-click` | `&` callback | — | `$event: XaSliceClickEvent` |
 */
import {
  computePie,
  CROSSHAIR_STROKE,
  ensureChartStyles,
  PIE_HOVER_OFFSET,
  normalizeSlices,
  pieTooltip,
  sliceAriaLabel,
} from '@xait/charts-core';
import type {
  XaPalette,
  XaPieLayout,
  XaPieWedge,
  XaSlice,
  XaSliceClickEvent,
  XaSliceHoverEvent,
} from '@xait/charts-core';
import { XaChartBaseController, XAC_SLICE_TABLE, XAC_TIP_TEMPLATE } from './chart-base';
import type { XaCallback } from './chart-base';
import type { XaChartsDefaults } from './config';

class XaPieController extends XaChartBaseController {
  static override $inject = ['$element', 'xaChartsConfig'];

  /* bindings */
  data?: XaSlice[];
  colors?: XaPalette;
  size?: number;
  labels?: 'inside' | 'leader' | 'none';
  legend?: 'bottom' | 'none';
  onSliceHover!: XaCallback<XaSliceHoverEvent>;
  onSliceClick!: XaCallback<XaSliceClickEvent>;

  /* view state */
  layout: XaPieLayout | null = null;
  legendPos: 'bottom' | 'none' = 'bottom';
  hoverLabel: string | null = null;
  ariaLabel = '';

  constructor($element: ng.IAugmentedJQuery, private readonly xaChartsConfig: XaChartsDefaults) {
    super($element);
  }

  $onInit(): void {
    ensureChartStyles();
    this.recompute();
  }

  $onChanges(): void {
    this.recompute();
  }

  /** Hovered wedge translates 5px outward along its mid-angle unit vector. */
  wedgeTransform(wedge: XaPieWedge): string {
    if (this.hoverLabel === wedge.label) {
      return `translate(${PIE_HOVER_OFFSET * wedge.ux} ${PIE_HOVER_OFFSET * wedge.uy})`;
    }
    return 'translate(0 0)';
  }

  hoverWedge(ev: MouseEvent, wedge: XaPieWedge): void {
    this.showTip(ev, pieTooltip(wedge));
    if (this.hoverLabel !== wedge.label) {
      this.hoverLabel = wedge.label;
      this.onSliceHover({ $event: { label: wedge.label } });
    }
  }

  leaveWedge(): void {
    this.hideTip();
    if (this.hoverLabel !== null) {
      this.hoverLabel = null;
      this.onSliceHover({ $event: { label: null } });
    }
  }

  clickWedge(wedge: XaPieWedge): void {
    this.onSliceClick({ $event: { label: wedge.label, value: wedge.value } });
  }

  private recompute(): void {
    if (this.data) {
      this.data = normalizeSlices(this.data);
    }
    this.legendPos = this.legend ?? 'bottom';
    if (!this.data) {
      this.layout = null;
      return;
    }
    this.layout = computePie(this.data, {
      size: this.size ?? 240,
      labels: this.labels ?? 'inside',
      colors: this.colors ?? this.xaChartsConfig.palette ?? 'mono',
    });
    this.ariaLabel = sliceAriaLabel('Pie', this.data);
  }
}

export const xaPieComponent: ng.IComponentOptions = {
  controller: XaPieController,
  bindings: {
    data: '<',
    colors: '<',
    size: '<',
    labels: '<',
    legend: '<',
    onSliceHover: '&',
    onSliceClick: '&',
  },
  template: `<div class="xac-chart">
  <svg ng-if="$ctrl.layout" class="xac-svg" role="img" aria-label="{{$ctrl.ariaLabel}}" ng-attr-width="{{$ctrl.layout.size}}" ng-attr-height="{{$ctrl.layout.size}}">
    <path ng-repeat="w in $ctrl.layout.wedges track by $index" class="xac-pie-wedge"
        ng-attr-d="{{w.path}}" ng-attr-fill="{{w.color}}" stroke="#fff" stroke-width="2"
        ng-attr-transform="{{$ctrl.wedgeTransform(w)}}"
        ng-mousemove="$ctrl.hoverWedge($event, w)"
        ng-mouseleave="$ctrl.leaveWedge()"
        ng-click="$ctrl.clickWedge(w)"></path>
    <g ng-repeat="l in $ctrl.layout.leaderLabels track by $index">
      <polyline fill="none" stroke="${CROSSHAIR_STROKE}" ng-attr-points="{{l.points}}"></polyline>
      <text class="xac-pielabel" ng-attr-text-anchor="{{l.anchor}}" ng-attr-x="{{l.textX}}" ng-attr-y="{{l.textY}}">{{l.text}}</text>
    </g>
    <text ng-repeat="l in $ctrl.layout.insideLabels track by $index" class="xac-pielabel-inside" text-anchor="middle" ng-attr-x="{{l.x}}" ng-attr-y="{{l.y}}">{{l.text}}</text>
  </svg>
  <xac-legend ng-if="$ctrl.layout && $ctrl.legendPos === 'bottom'" items="$ctrl.layout.legend" layout="'row'" toggleable="false" show-values="false"></xac-legend>
  ${XAC_SLICE_TABLE}
  ${XAC_TIP_TEMPLATE}
</div>`,
};
